import React from "react";

import {
  faChevronDown,
  faTimes,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnimatePresence, m } from "motion/react";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";

export type RunButtonRunState =
  | { type: "standby" }
  | { type: "running"; progress: number | null };

export type RunButtonDropdownState =
  | "closed"
  | "opened_by_click"
  | "opened_by_hover";

export type RunButtonDropdown = {
  state: RunButtonDropdownState;
  onStateChange: (dropdownState: RunButtonDropdownState) => void;
  children: React.ReactNode;
  disabled?: boolean;
};

type RunButtonProps = {
  state: RunButtonRunState;
  onRun: () => void;
  onStop: () => void;
  /** Called when the main segment is clicked while running and no dropdown is configured */
  onRunningClick?: () => void;
  dropdown?: RunButtonDropdown;
  runDisabled?: boolean;
  stopDisabled?: boolean;
  label?: string;
  runningLabel?: string;
  ref?: React.Ref<HTMLDivElement>;
  dataLoc?: string;
};

const MiniProgressBar: React.FC<{ progress: number | null }> = ({
  progress,
}) => (
  <m.div
    animate={{ width: 44 }}
    aria-valuenow={progress ?? undefined}
    className="flex gap-x-0.5"
    // Start and end with the same
    // width as the play button
    exit={{ width: 18 }}
    initial={{ width: 18 }}
    role="progressbar"
  >
    {progress === null ? (
      <div className="relative h-[3px] w-full overflow-hidden rounded-[100px] bg-white/20">
        <m.div
          animate={{ x: ["-100%", "100%"] }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
          transition={{ repeat: Infinity, duration: 1, ease: "easeInOut" }}
        />
      </div>
    ) : (
      <>
        {progress > 0 && (
          <m.div
            animate={{ width: `${progress}%` }}
            className="h-[3px] rounded-[100px] bg-white"
            initial={false}
          />
        )}
        <m.div
          animate={{ width: `${100 - progress}%` }}
          className="h-[3px] rounded-[100px] bg-white opacity-20"
          initial={false}
        />
      </>
    )}
  </m.div>
);

const PANEL_TRANSITION = {
  type: "tween",
  ease: "easeOut",
  duration: 0.15,
} as const;

const PANEL_VARIANTS = {
  visible: { opacity: 1, scale: 1, transition: PANEL_TRANSITION },
  hidden: { opacity: 0, scale: 0.95, transition: PANEL_TRANSITION },
};

/** The rounded pill shared by both modes. */
const Pill: React.FC<{
  children: React.ReactNode;
  layoutDependency: string;
  ref?: React.Ref<HTMLDivElement>;
  dataLoc?: string;
}> = ({ children, layoutDependency, ref, dataLoc }) => (
  <m.div
    ref={ref}
    className="relative flex items-center rounded-lg bg-indigo-600 font-inter-normal-13px text-white focus-within:ring-2 focus-within:ring-indigo-500/25"
    data-loc={dataLoc}
    layout
    layoutDependency={layoutDependency}
    // must match rounded-lg; motion only corrects corner distortion
    // during layout animations when the radius is set via style
    style={{ borderRadius: 8 }}
  >
    {children}
  </m.div>
);

/** The bordered icon area of a right segment (stop or dropdown trigger). */
const SegmentIcon: React.FC<{
  icon: IconDefinition;
  layoutDependency: string;
  dimmed?: boolean;
}> = ({ icon, layoutDependency, dimmed = false }) => (
  <m.div
    className={twJoin(
      "flex items-center border-indigo-500 border-l py-0.5 pr-3 pl-1.5",
      dimmed && "opacity-50",
    )}
    layout="position"
    layoutDependency={layoutDependency}
  >
    <Icon variant="inverted" icon={icon} size="2xs" />
  </m.div>
);

/**
 * Button that starts a run and morphs into a progress bar while the run is
 * in flight (`progress: null` renders an indeterminate shimmer). With the
 * optional `dropdown` it becomes a split button anchoring a popover panel.
 * Without it, a stop segment slides in only while running.
 * Purely presentational: the run lifecycle and the panel content live in
 * the consumer, which controls the dropdown through `dropdown.state`.
 */
export const RunButton: React.FC<RunButtonProps> = ({
  state,
  onRun,
  onStop,
  onRunningClick,
  dropdown,
  runDisabled = false,
  stopDisabled = false,
  label = "Test",
  runningLabel = "Testing...",
  ref,
  dataLoc,
}) => {
  const isRunning = state.type === "running";
  const loc = (suffix: string) =>
    dataLoc ? `${dataLoc}-${suffix}` : undefined;
  // Layout animations only need to re-measure when the button's natural
  // size can change, not on every render (e.g. progress updates)
  const layoutDependency = `${isRunning}-${isRunning ? runningLabel : label}-${Boolean(dropdown)}`;

  const toggleDropdown = () => {
    if (!dropdown || dropdown.disabled) {
      return;
    }

    if (dropdown.state === "closed") {
      dropdown.onStateChange("opened_by_click");
    } else {
      dropdown.onStateChange("closed");
    }
  };

  const handleStop = () => {
    if (!stopDisabled) {
      onStop();
    }
  };

  const handleMainClick = () => {
    if (!isRunning && !runDisabled) {
      onRun();
      return;
    }
    if (dropdown) {
      toggleDropdown();
      return;
    }
    if (isRunning) {
      onRunningClick?.();
    }
  };

  const mainButton = (
    <m.button
      className={twJoin(
        "flex cursor-pointer items-center overflow-hidden py-1.5 pl-3 focus:outline-none",
        dropdown || isRunning ? "pr-2" : "pr-4",
      )}
      data-loc={loc("button")}
      layout
      layoutDependency={layoutDependency}
      type="button"
      onClick={handleMainClick}
    >
      {/* Fixed height: the box must not change when the 10px play icon
          swaps for the 3px progress bar, or layout animates a vertical slide */}
      <m.div
        className="relative mr-1 flex h-2.5 items-center"
        layout="position"
        layoutDependency={layoutDependency}
      >
        <AnimatePresence initial={false} mode="wait">
          {!isRunning ? (
            <m.div
              key="play-icon"
              animate={{
                opacity: 1,
              }}
              initial={{ opacity: 0 }}
            >
              <Icon variant="inverted" icon={faPlay} size="2xs" />
            </m.div>
          ) : (
            <MiniProgressBar key="progress-bar" progress={state.progress} />
          )}
        </AnimatePresence>
      </m.div>
      <m.span
        className="whitespace-nowrap font-inter-medium-13px"
        layout="position"
        layoutDependency={layoutDependency}
      >
        {isRunning ? runningLabel : label}
      </m.span>
    </m.button>
  );

  if (!dropdown) {
    return (
      <Pill ref={ref} dataLoc={dataLoc} layoutDependency={layoutDependency}>
        {mainButton}
        <AnimatePresence initial={false}>
          {isRunning && (
            <m.button
              key="stop-button"
              animate={{ width: "auto", opacity: 1 }}
              aria-label="Stop run"
              className={twJoin(
                "flex items-center overflow-hidden py-1.5 focus:outline-none",
                stopDisabled ? "cursor-not-allowed" : "cursor-pointer",
              )}
              data-loc={loc("stop")}
              disabled={stopDisabled}
              exit={{ width: 0, opacity: 0 }}
              initial={{ width: 0, opacity: 0 }}
              type="button"
              onClick={handleStop}
            >
              <SegmentIcon
                dimmed={stopDisabled}
                icon={faTimes}
                layoutDependency={layoutDependency}
              />
            </m.button>
          )}
        </AnimatePresence>
      </Pill>
    );
  }

  return (
    <Popover
      ref={ref}
      as="div"
      className="relative"
      data-loc={dataLoc}
      onMouseEnter={() => {
        if (dropdown.disabled) {
          return;
        }

        if (dropdown.state === "closed" && isRunning)
          dropdown.onStateChange("opened_by_hover");
      }}
      onMouseLeave={() => {
        if (dropdown.state === "opened_by_hover")
          dropdown.onStateChange("closed");
      }}
    >
      <Pill layoutDependency={layoutDependency}>
        {/* This invisible element prevents the popover from being hidden when the mouse goes from the button to the popover panel */}
        <div className="absolute right-0 -bottom-3 left-0 h-3"></div>
        {mainButton}
        <PopoverButton
          aria-label={isRunning ? "Stop run" : "Open run options"}
          className={twJoin(
            "py-1.5 focus:outline-none",
            isRunning && stopDisabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
          data-loc={loc("dropdown")}
          disabled={isRunning && stopDisabled}
          type="button"
          onClick={() => {
            if (isRunning) {
              handleStop();
              return;
            }
            toggleDropdown();
          }}
        >
          <SegmentIcon
            dimmed={isRunning && stopDisabled}
            icon={isRunning ? faTimes : faChevronDown}
            layoutDependency={layoutDependency}
          />
        </PopoverButton>
      </Pill>
      <AnimatePresence>
        {dropdown.state !== "closed" && (
          <PopoverPanel
            anchor={{
              to: "bottom end",
              gap: 6,
            }}
            animate="visible"
            as={m.div}
            className="rounded-lg bg-white text-gray-800 shadow-2xl outline-none"
            data-loc={loc("dropdown-panel")}
            exit="hidden"
            initial="hidden"
            variants={PANEL_VARIANTS}
            static
          >
            {dropdown.children}
          </PopoverPanel>
        )}
      </AnimatePresence>
    </Popover>
  );
};
